import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SendOutlined } from '@ant-design/icons';
import { Button, Col, Input, App, Row, UploadFile } from 'antd';
import { TextAreaRef } from 'antd/es/input/TextArea';
import { ArgsProps } from 'antd/es/notification';

import { SmsServiceType } from '../../../../../shared/constants/enums';
import { AttachmentFile } from '../../../models';
import * as actions from '../../../store/actions';
import * as smsConstants from '../../../store/constants/sms-constants';
import UploadPhotos from '../../../upload-files';

import { AppDispatch, RootState } from '../../../../../store';

import { isEmpty } from 'lodash';

import './styles.less';

export type MessageTextAreaProps = {
  selectedPhoneNumbersAndNames: {
    phoneNumber: string;
    name: string;
    truckingCompanyId?: string;
  }[];
};

const MessageTextArea = ({ selectedPhoneNumbersAndNames }: MessageTextAreaProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { notification } = App.useApp();

  const messageRef = useRef<TextAreaRef | null>(null);

  const [textMessage, setTextMessage] = useState<string>();
  const [attachedPhotos, setAttachedPhotos] = useState<AttachmentFile[]>([]);
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);

  const sendMassMessagesResultLoading = useSelector((state: RootState) => state.smses.sendMassMessagesResultLoading);
  const messageControlParameters = useSelector((state: RootState) => state.smses.messageControlParameters);
  const messageControlParametersLoading = useSelector((state: RootState) => state.smses.messageControlParametersLoading);

  useEffect(() => {
    if (messageRef?.current) {
      messageRef.current.focus();
    }
  }, []);

  const onMessageChange = (messageValue: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextMessage(messageValue.target.value);
  };

  const onSandClick = () => {
    if (!messageControlParameters) {
      return;
    }

    dispatch(
      actions.sendMassMessages({
        sendMassMessageNumbers: selectedPhoneNumbersAndNames.map((x) => {
          return {
            toPhoneNumber: x.phoneNumber,
            toPhoneNumberName: x.name,
            truckingCompanyId: x.truckingCompanyId,
          };
        }),
        textMessage: textMessage?.trim(),
        attachedPhotos,
        smsServiceType: messageControlParameters.smsServiceType,
        myNumber: messageControlParameters.myNumber,
        phoneGroupSmsServiceType: messageControlParameters.phoneGroupSmsServiceType,
        phoneGroupId: messageControlParameters.phoneGroupId,
      }),
    ).then(() => {
      setTextMessage('');
      setAttachedPhotos([]);
      setFileList([]);

      notification.success({
        message: 'Success',
        top: 55,
        duration: 10,
        description: 'All messages have been sent successfully. Check the chat list for sent messages',
      } as ArgsProps);
    });
  };

  const onPressEnterTextArea = () => {
    if (
      selectedPhoneNumbersAndNames &&
      selectedPhoneNumbersAndNames.length > 0 &&
      (!isEmpty(textMessage?.trim()) || (attachedPhotos && attachedPhotos.length > 0))
    ) {
      onSandClick();
    }
  };

  return (
    <Row className="send-text-area-part">
      <Col span={24}>
        <Input.TextArea
          ref={messageRef}
          rows={3}
          value={textMessage}
          onChange={onMessageChange}
          className="send-text-area"
          variant="borderless"
          maxLength={
            messageControlParameters &&
            (messageControlParameters.smsServiceType === SmsServiceType.OwnRingCentral ||
              messageControlParameters.smsServiceType === SmsServiceType.AgencyOwnRingCentral ||
              messageControlParameters.smsServiceType === SmsServiceType.DedicatedRingCentral ||
              messageControlParameters.smsServiceType === SmsServiceType.DefaultRingCentral)
              ? smsConstants.MAX_MESSAGE_RING_CENTRAL_LENGTH
              : smsConstants.MAX_MESSAGE_LENGTH
          }
          disabled={!(selectedPhoneNumbersAndNames && selectedPhoneNumbersAndNames.length > 0) || sendMassMessagesResultLoading}
          onPressEnter={onPressEnterTextArea}
        />

        <Button
          className="send-message-button"
          icon={<SendOutlined />}
          type="primary"
          shape="circle"
          onClick={onPressEnterTextArea}
          title="Send message"
          disabled={
            (isEmpty(textMessage?.trim()) && !(attachedPhotos && attachedPhotos.length > 0)) ||
            !(selectedPhoneNumbersAndNames && selectedPhoneNumbersAndNames.length > 0)
          }
          loading={sendMassMessagesResultLoading}
        />

        {messageControlParameters &&
        messageControlParameters.smsServiceType !== SmsServiceType.DefaultRingCentral &&
        !messageControlParametersLoading ? (
          <UploadPhotos
            fileList={fileList}
            setAttachedPhotos={setAttachedPhotos}
            setFileList={setFileList}
            disabled={!(selectedPhoneNumbersAndNames && selectedPhoneNumbersAndNames.length > 0)}
          />
        ) : undefined}
      </Col>
    </Row>
  );
};

export default MessageTextArea;
